import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";

import PageLayout from "../../components/layout/PageLayout";
import { toast } from "react-toastify";

import { useGetServiceStatePriceByIdQuery } from "../../store/api/serviceStatePriceApi";

import {
  SERVICE_STATE_PRICES_EDIT_PATH,
  SERVICE_STATE_PRICES_LIST_PATH,
} from "../../navigations/path";

const formatPrice = (value) => {
  if (value === undefined || value === null || value === "") {
    return "—";
  }

  const price = Number(value);

  if (Number.isNaN(price)) {
    return value;
  }

  return `$${price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const ViewServiceStatePrice = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // =========================
  // GET SERVICE STATE PRICE BY ID
  // =========================
  const { data, isLoading, isError, error } = useGetServiceStatePriceByIdQuery(
    id,
    {
      skip: !id,
    },
  );

  // =========================
  // LOADING
  // =========================
  if (isLoading) {
    return (
      <PageLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={320}
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  // =========================
  // ERROR
  // =========================
  if (isError) {
    toast.error(error?.data?.message || "Failed to load service state price");

    return (
      <PageLayout>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(SERVICE_STATE_PRICES_LIST_PATH)}
          >
            Back to service state prices
          </Button>

          <Typography sx={{ mt: 3 }}>
            Failed to load service state price.
          </Typography>
        </Container>
      </PageLayout>
    );
  }

  // =========================
  // EXTRACT DATA
  // =========================
  const serviceStatePrice = data?.data ?? data;

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* BACK BUTTON */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(SERVICE_STATE_PRICES_LIST_PATH)}
          sx={{ mb: 2 }}
        >
          Back to service state prices
        </Button>

        {/* TITLE + EDIT BUTTON */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: "primary.main",
            }}
          >
            Service State Price Details
          </Typography>

          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() =>
              navigate(SERVICE_STATE_PRICES_EDIT_PATH.replace(":id", id))
            }
          >
            Edit Service State Price
          </Button>
        </Box>

        {/* DETAILS CARD */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* COUNTRY */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Country
                </Typography>

                <Typography variant="body1" fontWeight={500}>
                  {serviceStatePrice?.state?.country?.name ?? "—"}
                </Typography>
              </Grid>

              {/* STATE */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  State
                </Typography>

                <Typography variant="body1" fontWeight={500}>
                  {serviceStatePrice?.state?.name ?? "—"}
                </Typography>
              </Grid>

              {/* SERVICE PLAN */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Service Plan
                </Typography>

                <Typography variant="body1" fontWeight={500}>
                  {serviceStatePrice?.service_plan?.name ?? "—"}
                </Typography>
              </Grid>

              {/* SERVICE */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Service
                </Typography>

                <Typography variant="body1" fontWeight={500}>
                  {serviceStatePrice?.service?.name ?? "—"}
                </Typography>
              </Grid>

              {/* CURRENCY */}
              {/* <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Currency
                </Typography>

                <Typography variant="body1" fontWeight={500}>
                  {serviceStatePrice?.currency?.name ??
                    serviceStatePrice?.currency?.code ??
                    "—"}
                </Typography>
              </Grid> */}

              {/* PRICE */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Price
                </Typography>

                <Typography variant="body1" fontWeight={500}>
                  {formatPrice(serviceStatePrice?.price)}
                </Typography>
              </Grid>

              {/* GOVERNMENT FEE */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Government Fee
                </Typography>

                <Typography variant="body1" fontWeight={500}>
                  {formatPrice(serviceStatePrice?.government_fee)}
                </Typography>
              </Grid>

              {/* CREATED AT */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Created At
                </Typography>

                <Typography variant="body1" fontWeight={500}>
                  {serviceStatePrice?.created_at ?? "—"}
                </Typography>
              </Grid>

              {/* CREATED BY */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Created By
                </Typography>

                <Typography variant="body1" fontWeight={500}>
                  {serviceStatePrice?.author?.name ?? "—"}
                </Typography>
              </Grid>

              {/* DIVIDER */}
              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* DESCRIPTION */}
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Description
                </Typography>

                <Typography variant="body1">
                  {serviceStatePrice?.description ?? "—"}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewServiceStatePrice;
